import * as React from "react";
import DataTable, { TableColumn, ExpanderComponentProps } from "react-data-table-component";
import moment from "moment-timezone";
import NumberFormat from "react-number-format";
import { Form, Dropdown, Modal, Button } from "react-bootstrap";
import ApplicationStatus from "../../applications/components/ApplicationStatus";
import AddFavoriteCandidate from "../../favorites/components/AddFavoriteCandidate";
import { AllNotesModal } from "../../notes/components/ProfileNotes";
import type { Note } from "../../notes/components/ProfileNotes";
import SBSendMessage from "./SBChat/SBSendMessage";
import RpoManagedNotice from "./RpoManagedNotice";
import { ApplicantsContext } from "./ManageApplicants";
import type {
  ApplicantProfile as Profile,
  JobApplicationRecord as Applicant,
} from "../../../shared/types/domainModels";

// Columns marked `hide: MOBILE_BREAKPOINT` drop out at or below this width so
// the table fits a phone instead of running ~740px wide and forcing the page to
// scroll sideways. Matches the 768px breakpoint the page's stylesheet uses.
// Name, Status and the actions menu are always kept — they are what the list is
// for; location, rate, job and applied-date are recoverable by opening the row.
const MOBILE_BREAKPOINT = 768;

// Types
interface GroupedApplicant {
  profile: Profile;
  applications: Applicant[];
  applicationCount: number;
  earliestApplication: string;
  latestApplication: string;
  hasUnseen: boolean;
  // Staged RPO candidate on any of the grouped applications.
  rpoManaged: boolean;
}

interface ApplicantsTableProps {
  applicants: Applicant[];
  favorites: number[];
  authenticity_token: string;
  notes: Note[];
  load_notes: () => void;
  loaded: boolean;
  timezone: string;
  isHiddenList?: boolean;
  // Controlled filter mode: when provided, filters are managed externally (server-side)
  externalFilters?: { nameFilter: string; dateFilter: string; statusFilter: string; locationFilter?: string; positionTypeFilter?: string; jobTypeFilter?: string };
  onExternalFiltersChange?: (filters: any) => void;
  filterOptions?: { position_types?: string[]; job_types?: string[]; statuses?: string[] };
  isLoading?: boolean;
}

// Group applicants by profile
const groupApplicantsByProfile = (applicants: Applicant[]): GroupedApplicant[] => {
  const grouped = applicants.reduce((acc, applicant) => {
    const profileId = applicant.profile.id;
    if (!acc[profileId]) {
      acc[profileId] = {
        profile: applicant.profile,
        applications: [],
        applicationCount: 0,
        earliestApplication: applicant.created_at,
        latestApplication: applicant.created_at,
        hasUnseen: false,
        rpoManaged: false
      };
    }
    if (applicant.rpo_managed) {
      acc[profileId].rpoManaged = true;
    }
    acc[profileId].applications.push(applicant);
    acc[profileId].applicationCount++;
    if (!applicant.seen) {
      acc[profileId].hasUnseen = true;
    }
    // Update earliest/latest
    if (new Date(applicant.created_at) < new Date(acc[profileId].earliestApplication)) {
      acc[profileId].earliestApplication = applicant.created_at;
    }
    if (new Date(applicant.created_at) > new Date(acc[profileId].latestApplication)) {
      acc[profileId].latestApplication = applicant.created_at;
    }
    return acc;
  }, {} as Record<number, GroupedApplicant>);

  return Object.values(grouped);
};

// Expandable Job Applications Component
const ExpandedJobApplicationsComponent: React.FC<ExpanderComponentProps<GroupedApplicant> & {
  timezone: string;
  authenticity_token: string;
  updateStatus: (applicant: Applicant, status: string) => void;
}> = ({ data, timezone, authenticity_token, updateStatus }) => {
  const [showAll, setShowAll] = React.useState(false);
  const displayLimit = 5;
  const applicationsToShow = showAll ? data.applications : data.applications.slice(0, displayLimit);
  const hasMore = data.applications.length > displayLimit;

  return (
    <div style={{
      padding: '1.5rem',
      background: 'linear-gradient(to bottom, rgba(8, 94, 200, 0.02), rgba(23, 111, 201, 0.01))',
      borderTop: '1px solid #e8ecef'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        marginBottom: '1rem',
        color: '#034ba6',
        fontWeight: 600,
        fontSize: '0.875rem'
      }}>
        <i className="fa fa-briefcase"></i>
        Job Applications ({data.applications.length})
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {applicationsToShow.map((app) => {
          const job_title = app.job?.job_title || app.job?.job_type?.title || 'Unknown Job';
          const practice_name = app.job?.practice?.name || 'Unknown Practice';
          const application_date = moment(app.created_at).tz(timezone).format("ll");
          const updated_date = moment(app.updated_at).tz(timezone).format("ll");
          const dateString = app.withdrawn ? updated_date : application_date;
          const label = app.withdrawn ? "Withdrawn" : "Applied";

          return (
            <div
              key={app.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1.5fr 1fr 1.5fr 100px',
                gap: '1rem',
                alignItems: 'center',
                padding: '1rem',
                background: 'white',
                borderRadius: '0.5rem',
                border: '1px solid #e8ecef',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
              }}
            >
              {/* Job Title */}
              <div>
                <a
                  href={`/jobs/${app.job?.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#085ec8',
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    transition: 'color 0.3s ease'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.color = '#71ad08'}
                  onMouseOut={(e) => e.currentTarget.style.color = '#085ec8'}
                >
                  <i className="fa fa-briefcase me-2" style={{ color: '#085ec8' }}></i>
                  {job_title}
                </a>
                <div style={{ fontSize: '0.8rem', color: '#6c757d', marginTop: '0.25rem' }}>
                  {practice_name}
                </div>
              </div>

              {/* Applied Date */}
              <div style={{ fontSize: '0.875rem' }}>
                <span style={{ color: app.withdrawn ? '#dc3545' : '#6c757d', fontWeight: app.withdrawn ? 600 : 400 }}>
                  {label}
                </span>
                <br />
                {dateString}
              </div>

              {/* Withdrawn Badge */}
              <div>
                {app.withdrawn && (
                  <span className="badge-modern badge-modern--danger" style={{ whiteSpace: 'nowrap' }}>
                    Withdrawn
                  </span>
                )}
                {!app.seen && !app.withdrawn && (
                  <span className="badge-modern badge-modern--warning" style={{ whiteSpace: 'nowrap' }}>
                    New
                  </span>
                )}
              </div>

              {/* Status Dropdown */}
              <div>
                <ApplicationStatus application={app} updateStatusFunc={updateStatus} />
              </div>

              {/* Actions */}
              <div style={{ textAlign: 'center' }}>
                <a
                  href={`/jobs/${app.job?.id}#applicants`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '0.375rem',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    textDecoration: 'none',
                    background: 'linear-gradient(135deg, #085ec8, #176fc9)',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 6px rgba(8, 94, 200, 0.25)'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 10px rgba(8, 94, 200, 0.35)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 6px rgba(8, 94, 200, 0.25)';
                  }}
                >
                  <i className="fa fa-external-link me-1"></i>
                  View
                </a>
              </div>
            </div>
          );
        })}
      </div>

      {/* Show More / Show Less Button */}
      {hasMore && (
        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
          <button
            onClick={() => setShowAll(!showAll)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #085ec8, #176fc9)',
              color: 'white',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 6px rgba(8, 94, 200, 0.25)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 10px rgba(8, 94, 200, 0.35)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 6px rgba(8, 94, 200, 0.25)';
            }}
          >
            <i className={`fa fa-chevron-${showAll ? 'up' : 'down'}`}></i>
            {showAll ? 'Show Less' : `Show ${data.applications.length - displayLimit} More`}
          </button>
        </div>
      )}
    </div>
  );
};


// Column Definitions for Grouped Applicants
// ---------------------------------------------------------------------------
// Phone layout. Below MOBILE_BREAKPOINT the DataTables are replaced by cards:
// a three-column table at 390px squeezes names into ~60px columns that wrap
// letter by letter, and hiding more columns just hides the recruiter's data.
// Cards give each applicant the full width and keep every desktop action
// (favorite, notes, call, chat, profile, status select, delete/restore).
const useIsPhone = () => {
  const [isPhone, setIsPhone] = React.useState(
    () => typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT
  );
  React.useEffect(() => {
    const onResize = () => setIsPhone(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isPhone;
};

interface MobileCardsProps {
  groups: GroupedApplicant[];
  favorites: number[];
  authenticity_token: string;
  timezone: string;
  isHiddenList: boolean;
  hideProfile: (applicant: Applicant) => void;
  unhideProfile: (applicant: Applicant) => void;
  notes: Note[];
  onShowNotes: (profile: Profile) => void;
  updateStatus: (applicant: Applicant, status: string) => void;
}

function MobileApplicantCard({ group, favorites, authenticity_token, timezone, isHiddenList, hideProfile, unhideProfile, notes, onShowNotes, updateStatus }: MobileCardsProps & { group: GroupedApplicant }) {
  const p = group.profile;
  const profileNotes = notes.filter(note => note.profile_id === p.id);

  return (
    <div style={{
      background: 'white',
      border: '1px solid #e8ecef',
      borderRadius: '0.75rem',
      padding: '1rem',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)'
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
        <a href={`/profiles/${p.id}`} target="_blank" rel="noopener noreferrer" style={{ flexShrink: 0 }}>
          <img
            src={p.avatar_url}
            alt={p.fullname}
            className="rounded-circle"
            style={{ width: '48px', height: '48px', objectFit: 'cover', border: '2px solid #e8ecef' }}
          />
        </a>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <a
              href={`/profiles/${p.id}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#085ec8', fontWeight: 700, fontSize: '1rem', textDecoration: 'none' }}
            >
              {p.fullname}
            </a>
            {group.hasUnseen && (
              <span className="badge-modern badge-modern--warning" style={{ fontSize: '0.7rem' }}>New</span>
            )}
          </div>
          <div style={{ fontSize: '0.8rem', color: '#6c757d', marginTop: '0.125rem' }}>
            <i className="fa fa-map-marker me-1" style={{ color: '#085ec8' }}></i>
            {p.city}, {p.state}
            {p.hourly_rate ? (
              <span style={{ marginLeft: '0.5rem', fontWeight: 600, color: '#454f5b' }}>
                <i className="fa fa-money me-1" style={{ color: '#71ad08' }}></i>
                ${p.hourly_rate}/hr
              </span>
            ) : null}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>
          <div className="applicant-card__favorite">
            <AddFavoriteCandidate
              profile={p}
              favorited_init={favorites.includes(p.id)}
              authenticity_token={authenticity_token}
              expandDirection="left"
            />
          </div>
          {/* Same kebab pattern as the admin rows: secondary + destructive
              actions live here; only contact actions stay on the card. */}
          <Dropdown align="end">
            <Dropdown.Toggle
              as="button"
              type="button"
              className="context-menu-toggle"
              aria-label="More actions"
            >
              <i className="fa fa-ellipsis-v" aria-hidden="true"></i>
            </Dropdown.Toggle>
            <Dropdown.Menu className="context-menu">
              <Dropdown.Item as="button" type="button" className="context-menu__item" onClick={() => onShowNotes(p)}>
                <i className="fa fa-sticky-note" aria-hidden="true"></i>
                Notes{profileNotes.length > 0 ? ` (${profileNotes.length})` : ''}
              </Dropdown.Item>
              <Dropdown.Item className="context-menu__item" href={`/profiles/${p.id}`} target="_blank">
                <i className="fa fa-user" aria-hidden="true"></i>
                View Profile
              </Dropdown.Item>
              <Dropdown.Divider />
              {isHiddenList ? (
                <Dropdown.Item as="button" type="button" className="context-menu__item context-menu__item--success" onClick={() => group.applications.forEach(app => unhideProfile(app))}>
                  <i className="fa fa-recycle" aria-hidden="true"></i>
                  Restore
                </Dropdown.Item>
              ) : (
                <Dropdown.Item as="button" type="button" className="context-menu__item context-menu__item--danger" onClick={() => group.applications.forEach(app => hideProfile(app))}>
                  <i className="fa fa-trash" aria-hidden="true"></i>
                  Delete
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.75rem' }}>
        {group.applications.map((app) => (
          <div
            key={app.id}
            style={{
              border: '1px solid #eef2f6',
              borderRadius: '0.5rem',
              padding: '0.625rem 0.75rem',
              background: '#fbfcfe'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <a
                  href={`/jobs/${app.job?.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#085ec8', fontWeight: 600, fontSize: '0.875rem', textDecoration: 'none' }}
                >
                  {app.job?.job_title || app.job?.job_type?.title || 'Unknown Job'}
                </a>
                <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.125rem' }}>
                  {app.job?.practice?.name}
                  {' · '}
                  {app.withdrawn
                    ? `Withdrawn ${moment(app.updated_at).tz(timezone).format('ll')}`
                    : `Applied ${moment(app.created_at).tz(timezone).format('ll')}`}
                </div>
              </div>
              {/* Compact chip instead of a full-width select: the job title is
                  the content here; status is one small control off to the right. */}
              <ApplicationStatus application={app} updateStatusFunc={updateStatus} display="menu" />
            </div>
          </div>
        ))}
      </div>

      {/* Contact-first: reaching the candidate is THE action on this card, so
          Message (primary) and Call stay visible; everything secondary or
          destructive is in the header kebab, mirroring the admin rows. */}
      <div style={{ display: 'grid', gridTemplateColumns: p.phone ? '1fr 1fr' : '1fr', gap: '0.5rem', marginTop: '0.75rem' }}>
        {group.rpoManaged ? (
          <RpoManagedNotice className="text-muted small align-self-center" />
        ) : (
          <SBSendMessage profileId={p.id.toString()} variant="chip" chipClass="btn btn-sm btn-primary" />
        )}
        {p.phone && (
          <a className="btn btn-sm btn-outline-primary" href={`tel:${p.phone}`}>
            <i className="fa fa-phone me-1"></i>
            Call
          </a>
        )}
      </div>
    </div>
  );
}

function MobileApplicantCards(props: MobileCardsProps) {
  if (props.groups.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem 1rem', color: '#6c757d' }}>
        <i className="fa fa-users fa-2x mb-2" style={{ opacity: 0.4 }}></i>
        <div>No applicants found</div>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {props.groups.map(group => (
        <MobileApplicantCard key={group.profile.id} {...props} group={group} />
      ))}
    </div>
  );
}

const dataColumns = ({
  favorites,
  authenticity_token,
  timezone,
  isHiddenList,
  hideProfile,
  unhideProfile,
  notes,
  load_notes,
  loaded,
  onShowNotes
}: {
  favorites: number[];
  authenticity_token: string;
  timezone: string;
  isHiddenList: boolean;
  hideProfile: (applicant: Applicant) => void;
  unhideProfile: (applicant: Applicant) => void;
  notes: Note[];
  load_notes: () => void;
  loaded: boolean;
  onShowNotes: (profile: Profile) => void;
}): TableColumn<GroupedApplicant>[] => [
    {
      name: "Avatar",
      sortable: false,
      cell: (row) => {
        const profile_url = `/profiles/${row.profile.id}`;
        return (
          <a href={profile_url} target="_blank" rel="noopener noreferrer">
            <img
              src={row.profile.avatar_url}
              className="rounded-circle"
              alt={row.profile.fullname}
              style={{
                width: '60px',
                height: '60px',
                objectFit: 'cover',
                border: '3px solid #e8ecef',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => e.currentTarget.style.border = '3px solid #085ec8'}
              onMouseOut={(e) => e.currentTarget.style.border = '3px solid #e8ecef'}
            />
          </a>
        );
      },
      width: '90px',
      center: true,
      hide: MOBILE_BREAKPOINT
    },
    {
      name: "Name",
      selector: (row) => row.profile.fullname,
      sortable: true,
      cell: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <a
            href={`/profiles/${row.profile.id}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#085ec8',
              textDecoration: 'none',
              fontWeight: 700,
              fontSize: '1rem',
              transition: 'color 0.3s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.color = '#71ad08'}
            onMouseOut={(e) => e.currentTarget.style.color = '#085ec8'}
          >
            {row.profile.fullname}
          </a>
          <AddFavoriteCandidate
            profile={row.profile}
            favorited_init={favorites.includes(row.profile.id)}
            authenticity_token={authenticity_token}
            expandDirection="left"
          />
        </div>
      ),
      grow: 2
    },
    {
      name: "Location",
      selector: (row) => `${row.profile.city}, ${row.profile.state}`,
      sortable: true,
      cell: (row) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.875rem' }}>
          <i className="fa fa-map-marker" style={{ color: '#085ec8' }}></i>
          {row.profile.city}, {row.profile.state}
        </span>
      ),
      grow: 1.5,
      hide: MOBILE_BREAKPOINT
    },
    {
      name: "Desired Rate",
      selector: (row) => row.profile.hourly_rate || 0,
      sortable: true,
      cell: (row) => row.profile.hourly_rate ? (
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.875rem', fontWeight: 600 }}>
          <i className="fa fa-money" style={{ color: '#71ad08' }}></i>
          ${row.profile.hourly_rate}/hr
        </span>
      ) : (
        <span style={{ color: '#6c757d', fontSize: '0.875rem' }}>N/A</span>
      ),
      grow: 1,
      hide: MOBILE_BREAKPOINT
    },
    {
      name: "Applications",
      selector: (row) => row.applicationCount,
      sortable: true,
      cell: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span className="badge-modern badge-modern--info" style={{
            fontSize: '0.875rem',
            padding: '0.375rem 0.75rem'
          }}>
            {row.applicationCount} {row.applicationCount === 1 ? 'Job' : 'Jobs'}
          </span>
          {row.hasUnseen && (
            <span className="badge-modern badge-modern--warning" style={{ fontSize: '0.75rem' }}>
              New
            </span>
          )}
        </div>
      ),
      grow: 1.2
    },
    {
      name: "Actions",
      sortable: false,
      cell: (row) => {
        const phone_mask = row.profile.phone && row.profile.phone.length > 10 ? "+# (###) ###-####" : "(###) ###-####";
        const profileNotes = notes.filter(note => row.profile.id === note.profile_id);

        return (
          <div className="d-flex justify-content-center align-items-center gap-2" style={{ width: '100%' }}>
            <Dropdown>
              <Dropdown.Toggle
                variant="primary"
                size="sm"
                style={{
                  fontSize: '0.875rem',
                  padding: '0.5rem 0.875rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  color: 'white',
                  background: 'linear-gradient(135deg, #085ec8, #176fc9)',
                  transition: 'all 0.3s ease',
                  fontWeight: 600,
                  boxShadow: '0 2px 6px rgba(8, 94, 200, 0.25)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.375rem'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 10px rgba(8, 94, 200, 0.35)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 6px rgba(8, 94, 200, 0.25)';
                }}
              >
                <i className="fa fa-ellipsis-h"></i>
                Actions
              </Dropdown.Toggle>
              <Dropdown.Menu align="end" className="context-menu">
                <Dropdown.Item as="button" type="button" className="context-menu__item" onClick={() => onShowNotes(row.profile)}>
                  <i className="fa fa-sticky-note" aria-hidden="true"></i>
                  Notes {profileNotes.length > 0 && `(${profileNotes.length})`}
                </Dropdown.Item>
                <Dropdown.Divider />
                {row.profile.phone && (
                  <Dropdown.Item className="context-menu__item" href={`tel:${row.profile.phone}`}>
                    <i className="fa fa-phone" aria-hidden="true"></i>
                    <NumberFormat
                      value={row.profile.phone}
                      displayType="text"
                      type="text"
                      format={phone_mask}
                      mask="_"
                    />
                  </Dropdown.Item>
                )}
                {row.rpoManaged ? (
                  <Dropdown.ItemText className="context-menu__item">
                    <RpoManagedNotice />
                  </Dropdown.ItemText>
                ) : (
                  <Dropdown.Item as="div" className="context-menu__item" style={{ cursor: 'pointer' }}>
                    <SBSendMessage profileId={row.profile.id.toString()} variant="link" />
                  </Dropdown.Item>
                )}
                <Dropdown.Item className="context-menu__item" href={`/profiles/${row.profile.id}`} target="_blank">
                  <i className="fa fa-user" aria-hidden="true"></i>
                  View Profile
                </Dropdown.Item>
                <Dropdown.Divider />
                {isHiddenList ? (
                  <Dropdown.Item
                    as="button"
                    type="button"
                    className="context-menu__item context-menu__item--success"
                    onClick={() => row.applications.forEach(app => unhideProfile(app))}
                  >
                    <i className="fa fa-recycle" aria-hidden="true"></i>
                    Restore All
                  </Dropdown.Item>
                ) : (
                  <Dropdown.Item
                    as="button"
                    type="button"
                    className="context-menu__item context-menu__item--danger"
                    onClick={() => row.applications.forEach(app => hideProfile(app))}
                  >
                    <i className="fa fa-trash" aria-hidden="true"></i>
                    Delete All
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        );
      },
      grow: 1,
      center: true
    }
  ];

// Compact Column Definitions (flat, one row per application)
const compactColumns = ({
  favorites,
  authenticity_token,
  timezone,
  isHiddenList,
  hideProfile,
  unhideProfile,
  notes,
  load_notes,
  loaded,
  onShowNotes,
  updateStatus
}: {
  favorites: number[];
  authenticity_token: string;
  timezone: string;
  isHiddenList: boolean;
  hideProfile: (applicant: Applicant) => void;
  unhideProfile: (applicant: Applicant) => void;
  notes: Note[];
  load_notes: () => void;
  loaded: boolean;
  onShowNotes: (profile: Profile) => void;
  updateStatus: (applicant: Applicant, status: string) => void;
}): TableColumn<Applicant>[] => [
    {
      name: "Name",
      selector: (row) => row.profile.fullname,
      sortable: true,
      cell: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <a href={`/profiles/${row.profile.id}`} target="_blank" rel="noopener noreferrer">
            <img
              src={row.profile.avatar_url}
              className="rounded-circle"
              alt={row.profile.fullname}
              style={{ width: '32px', height: '32px', objectFit: 'cover', border: '2px solid #e8ecef' }}
            />
          </a>
          <div>
            <a
              href={`/profiles/${row.profile.id}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#085ec8', textDecoration: 'none', fontWeight: 600, fontSize: '0.875rem' }}
            >
              {row.profile.fullname}
            </a>
            <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
              {row.profile.city}, {row.profile.state}
            </div>
          </div>
        </div>
      ),
      grow: 1.5
    },
    {
      name: "Job",
      selector: (row) => row.job?.job_title || '',
      sortable: true,
      cell: (row) => (
        <div>
          <a
            href={`/jobs/${row.job?.id}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#085ec8', textDecoration: 'none', fontWeight: 600, fontSize: '0.875rem' }}
          >
            {row.job?.job_title || row.job?.job_type?.title || 'Unknown'}
          </a>
          <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
            {row.job?.practice?.name}
          </div>
        </div>
      ),
      grow: 1.5,
      hide: MOBILE_BREAKPOINT
    },
    {
      name: "Applied",
      selector: (row) => row.created_at,
      sortable: true,
      cell: (row) => {
        const date = moment(row.created_at).tz(timezone);
        return (
          <div style={{ fontSize: '0.8rem' }}>
            <div>{date.format("ll")}</div>
            {!row.seen && !row.withdrawn && (
              <span className="badge-modern badge-modern--warning" style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem' }}>New</span>
            )}
            {row.withdrawn && (
              <span className="badge-modern badge-modern--danger" style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem' }}>Withdrawn</span>
            )}
          </div>
        );
      },
      width: '120px',
      hide: MOBILE_BREAKPOINT
    },
    {
      name: "Status",
      cell: (row) => (
        <ApplicationStatus application={row} updateStatusFunc={updateStatus} />
      ),
      grow: 1.2
    },
    {
      name: "",
      sortable: false,
      cell: (row) => {
        const profileNotes = notes.filter(note => row.profile.id === note.profile_id);
        return (
          <div className="d-flex align-items-center gap-1">
            <AddFavoriteCandidate
              profile={row.profile}
              favorited_init={favorites.includes(row.profile.id)}
              authenticity_token={authenticity_token}
              expandDirection="left"
            />
            <Dropdown>
              <Dropdown.Toggle as="button" type="button" className="context-menu-toggle" aria-label="More actions">
                <i className="fa fa-ellipsis-v" aria-hidden="true"></i>
              </Dropdown.Toggle>
              <Dropdown.Menu align="end" className="context-menu">
                <Dropdown.Item as="button" type="button" className="context-menu__item" onClick={() => onShowNotes(row.profile)}>
                  <i className="fa fa-sticky-note" aria-hidden="true"></i>
                  Notes {profileNotes.length > 0 && `(${profileNotes.length})`}
                </Dropdown.Item>
                {row.profile.phone && (
                  <Dropdown.Item className="context-menu__item" href={`tel:${row.profile.phone}`}>
                    <i className="fa fa-phone" aria-hidden="true"></i>
                    Call
                  </Dropdown.Item>
                )}
                {row.rpo_managed ? (
                  <Dropdown.ItemText className="context-menu__item">
                    <RpoManagedNotice />
                  </Dropdown.ItemText>
                ) : (
                  <Dropdown.Item as="div" className="context-menu__item" style={{ cursor: 'pointer' }}>
                    <SBSendMessage profileId={row.profile.id.toString()} variant="link" />
                  </Dropdown.Item>
                )}
                <Dropdown.Item className="context-menu__item" href={`/profiles/${row.profile.id}`} target="_blank">
                  <i className="fa fa-user" aria-hidden="true"></i>
                  Profile
                </Dropdown.Item>
                <Dropdown.Divider />
                {isHiddenList ? (
                  <Dropdown.Item as="button" type="button" className="context-menu__item context-menu__item--success" onClick={() => unhideProfile(row)}>
                    <i className="fa fa-recycle" aria-hidden="true"></i>
                    Restore
                  </Dropdown.Item>
                ) : (
                  <Dropdown.Item as="button" type="button" className="context-menu__item context-menu__item--danger" onClick={() => hideProfile(row)}>
                    <i className="fa fa-trash" aria-hidden="true"></i>
                    Delete
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        );
      },
      width: '100px',
      right: true
    }
  ];

// Filter Bar Component
type ViewMode = 'standard' | 'compact';

interface FilterBarProps {
  filters: {
    nameFilter: string;
    dateFilter: string;
    statusFilter: string;
    locationFilter?: string;
    positionTypeFilter?: string;
    jobTypeFilter?: string;
  };
  onFiltersChange: (filters: any) => void;
  filterOptions?: {
    position_types?: string[];
    job_types?: string[];
    statuses?: string[];
  };
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
}

const POSITION_TYPE_LABELS: Record<string, string> = {
  fulltime: 'Full Time',
  parttime: 'Part Time',
  temp: 'Temporary',
  temporary: 'Temporary',
  longtermtemp: 'Long Term Temp',
};

const selectStyle = {
  marginBottom: 0,
  borderRadius: '0.5rem',
  border: '1px solid #e8ecef',
  padding: '0.5rem 0.75rem',
  fontSize: '0.95rem',
  transition: 'all 0.3s ease'
};

function FilterBar({ filters, onFiltersChange, filterOptions, viewMode, onViewModeChange }: FilterBarProps) {
  // On phones the six filter controls stack full-width and fill two screens
  // before any applicant is visible, so they collapse behind a toggle. Open by
  // default only when a filter is already active (so an applied filter is
  // never invisible).
  const isPhone = useIsPhone();
  const [filtersOpen, setFiltersOpen] = React.useState(false);

  const clearAllFilters = () => {
    onFiltersChange({
      nameFilter: "",
      dateFilter: "",
      statusFilter: "",
      locationFilter: "",
      positionTypeFilter: "",
      jobTypeFilter: ""
    });
  };

  const hasActiveFilters = filters.nameFilter || filters.dateFilter || filters.statusFilter ||
    filters.locationFilter || filters.positionTypeFilter || filters.jobTypeFilter;
  const activeFilterCount = [
    filters.nameFilter, filters.dateFilter, filters.statusFilter,
    filters.locationFilter, filters.positionTypeFilter, filters.jobTypeFilter
  ].filter(Boolean).length;
  const showControls = !isPhone || filtersOpen || Boolean(hasActiveFilters);

  return (
    <div className="management-filter-bar">
      {isPhone && (
        <button
          type="button"
          className="management-filter-bar__toggle"
          onClick={() => setFiltersOpen(open => !open)}
          aria-expanded={Boolean(showControls)}
        >
          <span><i className="fa fa-filter me-2"></i>Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}</span>
          <i className={`fa fa-chevron-${showControls ? 'up' : 'down'}`}></i>
        </button>
      )}
      <div
        className={`management-filter-bar__controls${showControls ? '' : ' management-filter-bar__controls--collapsed'}`}
      >
          <div className="management-filter-bar__field" style={{ position: "relative" }}>
            <Form.Group style={{ marginBottom: 0 }}>
              <div className="position-relative">
                <Form.Control
                  type="text"
                  placeholder="Search by name"
                  value={filters.nameFilter}
                  onChange={(e) => onFiltersChange({ ...filters, nameFilter: e.target.value })}
                  style={{
                    marginBottom: 0,
                    borderRadius: '0.5rem',
                    border: '1px solid #e8ecef',
                    padding: '0.5rem 0.75rem',
                    fontSize: '0.95rem',
                    transition: 'all 0.3s ease'
                  }}
                />
                {filters.nameFilter && (
                  <div
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      zIndex: 5,
                      color: '#6c757d'
                    }}
                    onClick={() => onFiltersChange({ ...filters, nameFilter: "" })}
                  >
                    <i className="fa fa-times" aria-hidden="true"></i>
                  </div>
                )}
              </div>
            </Form.Group>
          </div>
          <div className="management-filter-bar__field" style={{ position: "relative" }}>
            <Form.Group style={{ marginBottom: 0 }}>
              <div className="position-relative">
                <Form.Control
                  type="text"
                  placeholder="Search by location"
                  value={filters.locationFilter || ''}
                  onChange={(e) => onFiltersChange({ ...filters, locationFilter: e.target.value })}
                  style={{
                    marginBottom: 0,
                    borderRadius: '0.5rem',
                    border: '1px solid #e8ecef',
                    padding: '0.5rem 0.75rem',
                    fontSize: '0.95rem',
                    transition: 'all 0.3s ease'
                  }}
                />
                {filters.locationFilter && (
                  <div
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      zIndex: 5,
                      color: '#6c757d'
                    }}
                    onClick={() => onFiltersChange({ ...filters, locationFilter: "" })}
                  >
                    <i className="fa fa-times" aria-hidden="true"></i>
                  </div>
                )}
              </div>
            </Form.Group>
          </div>
          <div className="management-filter-bar__field" style={{ position: "relative" }}>
            <Form.Group style={{ marginBottom: 0 }}>
              <Form.Select
                value={filters.dateFilter}
                onChange={(e) => onFiltersChange({ ...filters, dateFilter: e.target.value })}
                style={selectStyle}
              >
                <option value="">All Dates</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </Form.Select>
            </Form.Group>
          </div>
          <div className="management-filter-bar__field" style={{ position: "relative" }}>
            <Form.Group style={{ marginBottom: 0 }}>
              <Form.Select
                value={filters.statusFilter}
                onChange={(e) => onFiltersChange({ ...filters, statusFilter: e.target.value })}
                style={selectStyle}
              >
                <option value="">All Statuses</option>
                {filterOptions?.statuses?.filter(s => s !== '').map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
          <div className="management-filter-bar__field" style={{ position: "relative" }}>
            <Form.Group style={{ marginBottom: 0 }}>
              <Form.Select
                value={filters.positionTypeFilter || ''}
                onChange={(e) => onFiltersChange({ ...filters, positionTypeFilter: e.target.value })}
                style={selectStyle}
              >
                <option value="">All Positions</option>
                {filterOptions?.position_types?.map(pt => (
                  <option key={pt} value={pt}>{POSITION_TYPE_LABELS[pt] || pt}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
          <div className="management-filter-bar__field" style={{ position: "relative" }}>
            <Form.Group style={{ marginBottom: 0 }}>
              <Form.Select
                value={filters.jobTypeFilter || ''}
                onChange={(e) => onFiltersChange({ ...filters, jobTypeFilter: e.target.value })}
                style={selectStyle}
              >
                <option value="">All Job Types</option>
                {filterOptions?.job_types?.map(jt => (
                  <option key={jt} value={jt}>{jt}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
          <button
            className="btn-modern btn-modern--ghost btn-modern--sm"
            onClick={clearAllFilters}
            style={{
              opacity: hasActiveFilters ? 1 : 0.6
            }}
            disabled={!hasActiveFilters}
          >
            <i className="fa fa-times"></i> Clear
          </button>
        {!isPhone && (
        <div className="d-flex align-items-center" style={{ gap: '0.25rem', flexShrink: 0 }}>
          <button
            onClick={() => onViewModeChange('standard')}
            title="Standard View"
            style={{
              padding: '0.4rem 0.6rem',
              borderRadius: '0.375rem 0 0 0.375rem',
              border: '1px solid #e8ecef',
              background: viewMode === 'standard' ? 'linear-gradient(135deg, #085ec8, #176fc9)' : 'white',
              color: viewMode === 'standard' ? 'white' : '#6c757d',
              cursor: 'pointer',
              fontSize: '0.875rem',
              transition: 'all 0.2s ease'
            }}
          >
            <i className="fa fa-th-large"></i>
          </button>
          <button
            onClick={() => onViewModeChange('compact')}
            title="Compact View"
            style={{
              padding: '0.4rem 0.6rem',
              borderRadius: '0 0.375rem 0.375rem 0',
              border: '1px solid #e8ecef',
              borderLeft: 'none',
              background: viewMode === 'compact' ? 'linear-gradient(135deg, #085ec8, #176fc9)' : 'white',
              color: viewMode === 'compact' ? 'white' : '#6c757d',
              cursor: 'pointer',
              fontSize: '0.875rem',
              transition: 'all 0.2s ease'
            }}
          >
            <i className="fa fa-list"></i>
          </button>
        </div>
        )}
      </div>
    </div>
  );
}

// Main Component
export default function ApplicantsTable({
  applicants,
  favorites,
  authenticity_token,
  notes,
  load_notes,
  loaded,
  timezone,
  isHiddenList = false,
  externalFilters,
  onExternalFiltersChange,
  filterOptions,
  isLoading = false
}: ApplicantsTableProps) {
  const { hideProfile, unhideProfile, updateStatus } = React.useContext(ApplicantsContext);
  const isPhone = useIsPhone();

  const [showNotesModal, setShowNotesModal] = React.useState(false);
  const [selectedProfile, setSelectedProfile] = React.useState<Profile | null>(null);

  // View mode with localStorage persistence
  const [viewMode, setViewMode] = React.useState<ViewMode>(() => {
    try {
      const saved = localStorage.getItem('applicants_view_mode');
      return (saved === 'compact' || saved === 'standard') ? saved : 'standard';
    } catch { return 'standard'; }
  });

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    try { localStorage.setItem('applicants_view_mode', mode); } catch {}
  };

  // Use external (server-side) filters when provided, otherwise use internal (client-side) state
  const isServerFiltered = !!onExternalFiltersChange;
  const [internalFilters, setInternalFilters] = React.useState({
    nameFilter: "",
    dateFilter: "",
    statusFilter: ""
  });

  const filters = isServerFiltered ? externalFilters! : internalFilters;
  const setFilters = isServerFiltered ? onExternalFiltersChange! : setInternalFilters;

  const handleShowNotes = (profile: Profile) => {
    setSelectedProfile(profile);
    setShowNotesModal(true);
  };

  // Client-side filter logic — only used when NOT server-filtered
  const filteredApplicants = React.useMemo(() => {
    if (isServerFiltered) return applicants; // Server already filtered

    return applicants.filter((applicant) => {
      if (filters.nameFilter && !applicant.profile.fullname.toLowerCase().includes(filters.nameFilter.toLowerCase())) {
        return false;
      }

      if (filters.dateFilter) {
        const appDate = moment(applicant.created_at);
        const now = moment();

        switch (filters.dateFilter) {
          case "today":
            if (!appDate.isSame(now, 'day')) return false;
            break;
          case "week":
            if (!appDate.isSame(now, 'week')) return false;
            break;
          case "month":
            if (!appDate.isSame(now, 'month')) return false;
            break;
        }
      }

      if (filters.statusFilter && applicant.status !== filters.statusFilter) {
        return false;
      }

      return true;
    });
  }, [applicants, filters, isServerFiltered]);

  // Group applicants by profile (for standard view)
  const groupedApplicants = React.useMemo(() => {
    return groupApplicantsByProfile(filteredApplicants);
  }, [filteredApplicants]);

  // Sort: profiles with unseen applications first, then by latest application date
  const sortedGroupedApplicants = React.useMemo(() => {
    return [...groupedApplicants].sort((a, b) => {
      if (a.hasUnseen && !b.hasUnseen) return -1;
      if (!a.hasUnseen && b.hasUnseen) return 1;
      return new Date(b.latestApplication).getTime() - new Date(a.latestApplication).getTime();
    });
  }, [groupedApplicants]);

  const standardCols = dataColumns({
    favorites,
    authenticity_token,
    timezone,
    isHiddenList,
    hideProfile,
    unhideProfile,
    notes,
    load_notes,
    loaded,
    onShowNotes: handleShowNotes
  });

  const compactCols = compactColumns({
    favorites,
    authenticity_token,
    timezone,
    isHiddenList,
    hideProfile,
    unhideProfile,
    notes,
    load_notes,
    loaded,
    onShowNotes: handleShowNotes,
    updateStatus
  });

  const tableContainerStyle = {
    overflow: 'visible' as const,
    display: isLoading ? 'none' : undefined
  };

  const sharedCustomStyles = {
    responsiveWrapper: { style: { overflowX: 'visible' as const, overflowY: 'visible' as const } },
    table: { style: { width: '100%', tableLayout: 'auto' as const } },
    headCells: { style: { fontSize: '0.875rem', fontWeight: 600, color: '#6c757d', paddingTop: '0.75rem', paddingBottom: '0.75rem', paddingLeft: '0.5rem', paddingRight: '0.5rem' } },
    cells: { style: { paddingLeft: '0.5rem', paddingRight: '0.5rem', paddingTop: '0.75rem', paddingBottom: '0.75rem', overflow: 'visible' as const } },
  };

  return (
    <div>
      <FilterBar
        filters={filters}
        onFiltersChange={setFilters}
        filterOptions={filterOptions}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
      />
      {isLoading && (
        <div className="d-flex justify-content-center my-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {!isLoading && filteredApplicants.length > 0 && (isPhone ? (
        <div>
          <MobileApplicantCards
            groups={sortedGroupedApplicants}
            favorites={favorites}
            authenticity_token={authenticity_token}
            timezone={timezone}
            isHiddenList={isHiddenList}
            hideProfile={hideProfile}
            unhideProfile={unhideProfile}
            notes={notes}
            onShowNotes={handleShowNotes}
            updateStatus={updateStatus}
          />
        </div>
      ) : viewMode === 'standard' ? (
        <div className="profile-list-modern management-data-surface" style={tableContainerStyle}>
          <DataTable
            data={sortedGroupedApplicants}
            columns={standardCols}
            defaultSortFieldId={5}
            defaultSortAsc={false}
            expandableRows
            expandableRowExpanded={() => true}
            expandableRowsComponent={(props) => (
              <ExpandedJobApplicationsComponent
                {...props}
                timezone={timezone}
                authenticity_token={authenticity_token}
                updateStatus={updateStatus}
              />
            )}
            responsive
            customStyles={{
              ...sharedCustomStyles,
              expanderButton: { style: { color: '#085ec8', '&:hover': { color: '#71ad08' } } }
            }}
          />
        </div>
      ) : (
        <div className="profile-list-modern management-data-surface" style={tableContainerStyle}>
          <DataTable
            data={filteredApplicants}
            columns={compactCols}
            defaultSortFieldId={3}
            defaultSortAsc={false}
            responsive
            dense
            customStyles={{
              ...sharedCustomStyles,
              cells: { style: { ...sharedCustomStyles.cells.style, paddingTop: '0.5rem', paddingBottom: '0.5rem' } },
            }}
          />
        </div>
      ))}

      {/* All Notes Modal */}
      {selectedProfile && (
        <AllNotesModal
          show={showNotesModal}
          onHide={() => setShowNotesModal(false)}
          profile={selectedProfile}
          notes={notes.filter(note => selectedProfile.id === note.profile_id)}
          authenticity_token={authenticity_token}
          loadNotesCallback={load_notes}
        />
      )}
    </div>
  );
}
